import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { GoalCard } from "@/pages/talent/goals/components/GoalCard.tsx";
import { PerformanceReviewUnsolicitedTag } from "@/pages/talent/performance-reviews/view/components/PerformanceReviewUnsolicitedTag.tsx";
import {
  EmptyStateBase,
  Modal,
  ModalProps,
  Box,
  Button,
  EmojiDisplay,
  HtmlViewer,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { linkifyText } from "@/helpers/utils.helper.ts";
import { usePerformanceReviewQuestionResultsSearchInfinite } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { ApiDepartmentShort } from "@/types/department.types.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiFormElementAggregation } from "@/types/forms/form-result.types.ts";
import { ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import { ApiFormElementType } from "@/types/forms/form.types.ts";
import {
  ApiPerformanceReviewResultSearchItem,
  ApiPerformanceReviewResultsFilter,
} from "@/types/performance-review.types.ts";
import { ReactElement, useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface PerformanceReviewQuestionResultsModalProps extends ModalProps {
  performanceReviewId: number;
  reviewTypeId?: number;
  element: ApiFormViewElement;
  elementIds: number[];
  referenceId?: number;
  participantId?: number;
  answerScaleOptionIds?: number[];
  answerSelectOptionIds?: number[];
  selectableOptionIds?: number[];
  resultAggregation?: ApiFormElementAggregation | null;
  managers?: ApiEmployeeShort[];
  departments?: ApiDepartmentShort[];
}

export function PerformanceReviewQuestionResultsModal({
  performanceReviewId,
  reviewTypeId,
  element,
  elementIds,
  participantId,
  referenceId,
  answerScaleOptionIds,
  answerSelectOptionIds,
  selectableOptionIds,
  resultAggregation,
  managers,
  departments,
  ...props
}: PerformanceReviewQuestionResultsModalProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const getInitialFilterOptionIds = (): number[] | null => {
    switch (element.type) {
      case ApiFormElementType.scale:
        return answerScaleOptionIds ?? null;
      case ApiFormElementType.select:
        return answerSelectOptionIds ?? null;
      case ApiFormElementType.employee_nps:
        return selectableOptionIds ?? null;
      default:
        return null;
    }
  };

  const [selectedFilterOptionIds, setSelectedFilterOptionIds] = useState<number[] | null>(
    getInitialFilterOptionIds
  );

  const filterOptions = useMemo(() => {
    switch (element.type) {
      case ApiFormElementType.scale:
        return (
          element.scale_settings?.form_scale.scale
            .map((option) => ({
              id: option.id,
              title: option.title,
              emoji: option.emoji,
            }))
            .sort((a, b) => a.id - b.id) ?? []
        );
      case ApiFormElementType.select:
        return (
          element.select_settings?.options.map((option) => ({
            id: option.id,
            title: option.title,
            emoji: option.emoji,
          })) ?? []
        );
      case ApiFormElementType.employee_nps:
        return Array.from({ length: 11 }, (_, i) => ({
          id: i,
          title: String(i),
          emoji: null,
        }));
      default:
        return [];
    }
  }, [element]);

  const getOptionPercent = useCallback(
    (optionId: number): number | null => {
      if (!resultAggregation) return null;

      let options: { selectable_option_id: number; count: number }[] | undefined;

      switch (element.type) {
        case ApiFormElementType.scale:
          options = resultAggregation.scale_data?.options;
          break;
        case ApiFormElementType.select:
          options = resultAggregation.select_data?.options;
          break;
        case ApiFormElementType.employee_nps:
          options = resultAggregation.employee_nps_data?.options;
          break;
      }

      if (!options) return null;

      const total = options.reduce((sum, opt) => sum + opt.count, 0);
      if (total === 0) return null;

      const option = options.find((opt) => opt.selectable_option_id === optionId);
      if (!option) return 0;

      return Math.round((option.count / total) * 100);
    },
    [resultAggregation, element.type]
  );

  const getFilterCaption = useMemo(() => {
    if (managers && managers.length > 0) {
      const managerNames = managers.map((m) => renderEmployeeName(m)).join(", ");
      return managers.length === 1
        ? t("surveys:filtered_by_manager", { name: managerNames })
        : t("surveys:filtered_by_managers", { names: managerNames });
    }
    if (departments && departments.length > 0) {
      const departmentNames = departments.map((d) => d.title).join(", ");
      return departments.length === 1
        ? t("surveys:filtered_by_department", { name: departmentNames })
        : t("surveys:filtered_by_departments", { names: departmentNames });
    }
    return undefined;
  }, [managers, departments]);

  const filter = useMemo<ApiPerformanceReviewResultsFilter>(() => {
    const baseFilter: ApiPerformanceReviewResultsFilter = {
      reference_id: referenceId ?? null,
      performance_review_id: performanceReviewId,
      reviewer_type_id: reviewTypeId ?? null,
      form_element_ids: Array.from(new Set([element.id, ...elementIds])),
      participant_id: participantId ?? null,
      page_number: 1,
      selectable_option_ids:
        element.type === ApiFormElementType.employee_nps ? undefined : selectableOptionIds,
      manager_ids: managers?.map((m) => m.id),
      department_ids: departments?.map((d) => d.id),
    };

    if (selectedFilterOptionIds !== null) {
      switch (element.type) {
        case ApiFormElementType.scale:
          baseFilter.answer_scale_option_ids = selectedFilterOptionIds;
          break;
        case ApiFormElementType.select:
          baseFilter.answer_select_option_ids = selectedFilterOptionIds;
          break;
        case ApiFormElementType.employee_nps:
          baseFilter.selectable_option_ids = selectedFilterOptionIds;
          break;
      }
    }

    return baseFilter;
  }, [
    referenceId,
    performanceReviewId,
    reviewTypeId,
    element.id,
    element.type,
    elementIds,
    participantId,
    selectableOptionIds,
    selectedFilterOptionIds,
    managers,
    departments,
  ]);

  const {
    data: results,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = usePerformanceReviewQuestionResultsSearchInfinite(filter);

  const fetchMoreOnBottomReached = useCallback(
    (scrollHeight: number, scrollTop: number, clientHeight: number) => {
      if (
        scrollHeight - scrollTop - clientHeight < 400 &&
        !isFetching &&
        hasNextPage &&
        !isFetchingNextPage
      ) {
        fetchNextPage();
      }
    },
    [fetchNextPage, isFetching, hasNextPage, isFetchingNextPage]
  );

  const getTitle = () => {
    let title: string | null = null;
    let subtitle: string | null;
    switch (element.type) {
      case ApiFormElementType.text:
        title = element.text_settings?.title || null;
        break;
      case ApiFormElementType.open_answer:
        title = element.open_answer_settings?.title || null;
        break;
      case ApiFormElementType.scale:
        title = element.scale_settings?.title || null;
        break;
      case ApiFormElementType.goals:
        title = element.goals_settings?.title || null;
        break;
      case ApiFormElementType.select:
        title = element.select_settings?.title || null;
        break;
      case ApiFormElementType.employee_nps:
        title =
          element.employee_nps_settings?.title ?? t("surveys:employee_nps_element_title_default");
        break;
    }

    if (results && results.pages.length > 0) {
      const totalAnswers = results.pages[0]?.metadata?.total_items;
      if (totalAnswers) {
        subtitle = t("plurals:people_answered", { count: totalAnswers });
      } else {
        subtitle = t("performance_review:no_answers_yet");
      }
    } else {
      subtitle = `${t("common:loading")}...`;
    }

    return (
      <Title
        header={title || "-"}
        caption={subtitle || undefined}
        size={"lg"}
        paddingBottom={true}
      />
    );
  };

  const flatData = useMemo(
    () => results?.pages?.flatMap((page) => page?.items ?? []) ?? [],
    [results]
  );

  const getAnswer = (answer: ApiPerformanceReviewResultSearchItem) => {
    if (!answer.answer) return <Text>-</Text>;

    switch (element.type) {
      case ApiFormElementType.open_answer:
        return (
          <Box className={"overflow-hidden"}>
            <HtmlViewer content={linkifyText(answer.answer.answer_text ?? "")} />
          </Box>
        );
      case ApiFormElementType.employee_nps:
        return <Text>{answer.answer.answer_int}</Text>;
      case ApiFormElementType.select: {
        const selectSettings = element.select_settings;
        if (!selectSettings || !answer.answer.answer_select_option_ids) return null;

        const selectedOptions = selectSettings.options.filter((option) =>
          answer.answer?.answer_select_option_ids?.includes(option.id)
        );

        const selectResult: ReactElement[] = [];

        for (const selectedOption of selectedOptions) {
          if (selectedOption.emoji) {
            selectResult.push(
              <EmojiDisplay
                key={`emoji-${selectedOption.id}`}
                size={"md"}
                id={selectedOption.emoji}
              />
            );
          } else if (selectedOption.title) {
            selectResult.push(
              <Text key={`title-${selectedOption.id}`}>{selectedOption.title}</Text>
            );
          }
        }

        return selectResult;
      }
      case ApiFormElementType.goals: {
        const goalsScaleSettings = element.goals_settings;

        if (!goalsScaleSettings || !answer.answer.answer_scale_option_ids) return null;

        const goalsSelectedOptions = goalsScaleSettings.form_scale.scale
          .filter((option) => answer.answer?.answer_scale_option_ids?.includes(option.id))
          .sort((a, b) => a.sort - b.sort);

        const goalsSelectResult: ReactElement[] = [];

        for (const selectedOption of goalsSelectedOptions) {
          if (selectedOption.emoji) {
            goalsSelectResult.push(
              <EmojiDisplay
                key={`emoji-${selectedOption.id}`}
                size={"md"}
                id={selectedOption.emoji}
              />
            );
          } else if (selectedOption.title) {
            goalsSelectResult.push(
              <Text key={`title-${selectedOption.id}`}>{selectedOption.title}</Text>
            );
          }
        }
        if (goalsSelectResult.length === 0) {
          return null;
        }

        return goalsSelectResult;
      }
      case ApiFormElementType.scale: {
        const scaleSettings = element.scale_settings;

        if (!scaleSettings || !answer.answer.answer_scale_option_ids) return null;

        const selectedOptions = scaleSettings.form_scale.scale
          .filter((option) => answer.answer?.answer_scale_option_ids?.includes(option.id))
          .sort((a, b) => a.sort - b.sort);

        const selectResult: ReactElement[] = [];

        for (const selectedOption of selectedOptions) {
          if (selectedOption.emoji) {
            selectResult.push(
              <EmojiDisplay
                key={`emoji-${selectedOption.id}`}
                size={"md"}
                id={selectedOption.emoji}
              />
            );
          } else if (selectedOption.title) {
            selectResult.push(
              <Text key={`title-${selectedOption.id}`}>{selectedOption.title}</Text>
            );
          }
        }
        if (selectResult.length === 0) {
          return null;
        }

        return selectResult;
      }
    }
  };

  return (
    <Modal
      title={t("surveys:answers_breakdown")}
      caption={getFilterCaption}
      {...props}
      layout="stretch"
      size={"lg"}
      withCloser={true}
      onModalContentScroll={(scrollTop, scrollHeight, height) => {
        fetchMoreOnBottomReached(scrollHeight, scrollTop, height);
      }}
    >
      {getTitle()}

      {filterOptions.length > 0 && !managers?.length && !departments?.length ? (
        <Stack direction={"horizontal"} gap={"sm"} className={"flex-wrap mb-4"}>
          <Button
            variant={selectedFilterOptionIds === null ? "primary" : "secondary"}
            onClick={() => setSelectedFilterOptionIds(null)}
          >
            {t("common:all")}
          </Button>
          {filterOptions.map((option) => {
            const percent = getOptionPercent(option.id);
            const isSelected = selectedFilterOptionIds?.includes(option.id) ?? false;
            return (
              <Button
                key={option.id}
                variant={isSelected ? "primary" : "secondary"}
                onClick={() => {
                  setSelectedFilterOptionIds((prev) => {
                    if (prev === null) return [option.id];
                    if (isSelected) {
                      const newIds = prev.filter((id) => id !== option.id);
                      return newIds.length > 0 ? newIds : null;
                    }
                    return [...prev, option.id];
                  });
                }}
              >
                <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                  {option.emoji ? <EmojiDisplay size={"sm"} id={option.emoji} /> : option.title}
                  <Text className={"font-normal"}>{percent !== null && ` — ${percent}%`}</Text>
                </Stack>
              </Button>
            );
          })}
        </Stack>
      ) : (
        <></>
      )}

      <Box ref={tableContainerRef} className={"overflow-y-auto h-full"}>
        <Stack gap={"md"}>
          {flatData.map((row, index) => (
            <Box variant={"border"} key={index}>
              <Stack gap={"sm"}>
                {row.employee && (
                  <Stack direction="horizontal" gap="xs" items="center">
                    <EmployeeItem
                      employee={row.employee}
                      direction={"horizontal"}
                      size={"sm"}
                      variant={"secondary"}
                    />
                    {row.is_unsolicited === true && <PerformanceReviewUnsolicitedTag />}
                  </Stack>
                )}
                {row.goal && (
                  <GoalCard
                    goal={row.goal}
                    disableDecoration={true}
                    target={"_blank"}
                    to={`/goals/${row.goal.id}`}
                  />
                )}
                <Box>{getAnswer(row)}</Box>
                {row.answer?.comment && (
                  <Text className={"whitespace-pre-wrap text-secondary"}>{row.answer.comment}</Text>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
        {(isLoading || isFetching) && <Skeleton className={"h-10 w-full"} />}
        {!isLoading && !isFetching && flatData.length === 0 && (
          <EmptyStateBase title={t("performance_review:no_answers_yet")} emoji={":mag:"} />
        )}
      </Box>
    </Modal>
  );
}
